import { useState, useEffect } from 'react';
import { useBusinessProfile } from './useBusinessProfiles';
import { 
  normalizeBrandVoice, 
  normalizeProductsServices, 
  parseAddressComponents,
  type ParsedAddress 
} from '../lib/businessProfileUtils';

export interface BusinessProfileFormData {
  business_name: string;
  business_type_id: number | null;
  description: string;
  brand_voice: string[];
  products_services: string[];
  website_url: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  target_audience: string[];
  key_messages: string[];
  content_goals: string[];
  preferred_platform_ids: number[];
}

export interface TagInputsState {
  brand_voice: string;
  target_audience: string;
  key_messages: string;
  content_goals: string;
  products_services: string;
}

export interface UseBusinessProfileFormReturn {
  formData: BusinessProfileFormData;
  setFormData: React.Dispatch<React.SetStateAction<BusinessProfileFormData>>;
  tagInputs: TagInputsState;
  setTagInputs: React.Dispatch<React.SetStateAction<TagInputsState>>;
  addressInput: string;
  setAddressInput: React.Dispatch<React.SetStateAction<string>>;
  strategyGenerated: boolean;
  setStrategyGenerated: React.Dispatch<React.SetStateAction<boolean>>;
  handleInputChange: (field: string, value: any) => void;
  handleAddressSelect: (place: any) => void;
  handleTagInputChange: (field: string, value: string) => void;
  handleTagAdd: (field: string) => void;
  handleTagRemove: (field: string, index: number) => void;
  handleTagKeyPress: (field: string, event: React.KeyboardEvent) => void;
  handlePlatformChange: (platformId: number, checked: boolean) => void;
  validateStep: (step: number) => boolean;
}

const initialFormData: BusinessProfileFormData = {
  business_name: '',
  business_type_id: null,
  description: '',
  brand_voice: [],
  products_services: [],
  website_url: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state_province: '',
  postal_code: '',
  country: '',
  target_audience: [],
  key_messages: [],
  content_goals: [],
  preferred_platform_ids: [],
};

const initialTagInputs: TagInputsState = {
  brand_voice: '',
  target_audience: '',
  key_messages: '',
  content_goals: '',
  products_services: '',
};

export function useBusinessProfileForm(profileId: number | null): UseBusinessProfileFormReturn {
  const [formData, setFormData] = useState<BusinessProfileFormData>(initialFormData);
  const [tagInputs, setTagInputs] = useState<TagInputsState>(initialTagInputs);
  const [addressInput, setAddressInput] = useState('');
  const [strategyGenerated, setStrategyGenerated] = useState(false);

  const { data: existingProfile } = useBusinessProfile(profileId?.toString() || '');
  const isEditMode = !!profileId;

  // Load existing profile data when in edit mode
  useEffect(() => {
    if (isEditMode && existingProfile) {
      setFormData({
        business_name: existingProfile.business_name || '',
        business_type_id: existingProfile.business_type_id || null,
        description: existingProfile.description || '',
        brand_voice: normalizeBrandVoice(existingProfile.brand_voice),
        products_services: normalizeProductsServices(existingProfile.products_services),
        website_url: existingProfile.website_url || '',
        address_line_1: existingProfile.address_line_1 || '',
        address_line_2: existingProfile.address_line_2 || '',
        city: existingProfile.city || '',
        state_province: existingProfile.state_province || '',
        postal_code: existingProfile.postal_code || '',
        country: existingProfile.country || '',
        target_audience: Array.isArray(existingProfile.target_audience) ? existingProfile.target_audience : [],
        key_messages: existingProfile.key_messages || [],
        content_goals: existingProfile.content_goals || [],
        preferred_platform_ids: existingProfile.preferred_platform_ids || [],
      });
      setStrategyGenerated(!!(existingProfile.target_audience?.length || existingProfile.key_messages?.length || existingProfile.content_goals?.length));
    }
  }, [isEditMode, existingProfile]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressSelect = (place: any) => {
    // console.log('📍 Selected place:', place);
    
    if (place) {
      // Parse address components from Google Places API format
      const parsedAddress: ParsedAddress = parseAddressComponents(place);
      
      // console.log('📍 Parsed address:', parsedAddress);
      // console.log('📍 Current formData before update:', formData);
      
      // Force a new object reference to ensure React detects the change
      setFormData(prev => ({
        ...prev,
        address_line_1: parsedAddress.address_line_1,
        address_line_2: parsedAddress.address_line_2,
        city: parsedAddress.city,
        state_province: parsedAddress.state_province,
        postal_code: parsedAddress.postal_code,
        country: parsedAddress.country,
      }));
      
      // Also update the search input to show the formatted address
      if (place.formatted_address) {
        setAddressInput(place.formatted_address);
      }
      
      // console.log('📍 formData update triggered with:', {
      //   address_line_1: parsedAddress.address_line_1,
      //   address_line_2: parsedAddress.address_line_2,
      //   city: parsedAddress.city,
      //   state_province: parsedAddress.state_province,
      //   postal_code: parsedAddress.postal_code,
      //   country: parsedAddress.country,
      // });
    }
  };

  const handleTagInputChange = (field: string, value: string) => {
    setTagInputs(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTagAdd = (field: string) => {
    const inputValue = tagInputs[field as keyof TagInputsState];
    const currentValue = formData[field as keyof BusinessProfileFormData];
    const maxTags = field === 'products_services' ? 10 : 5;
    if (inputValue.trim() && Array.isArray(currentValue) && currentValue.length < maxTags) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field as keyof BusinessProfileFormData] as string[]), inputValue.trim()],
      }));
      setTagInputs(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleTagRemove = (field: string, index: number) => {
    setFormData(prev => {
      const currentValue = prev[field as keyof BusinessProfileFormData];
      if (Array.isArray(currentValue)) {
        return {
          ...prev,
          [field]: currentValue.filter((_: any, i: number) => i !== index),
        };
      }
      return prev;
    });
  };

  const handleTagKeyPress = (field: string, event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleTagAdd(field);
    }
  };

  const handlePlatformChange = (platformId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      preferred_platform_ids: checked
        ? [...prev.preferred_platform_ids, platformId]
        : prev.preferred_platform_ids.filter(id => id !== platformId)
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Basic Information
        // Validate basic fields
        const basicFieldsValid = formData.business_name.trim().length >= 2 && 
                                  formData.business_type_id !== null &&
                                  formData.description.trim().length >= 3;
        
        // Validate address fields - require at least Address Line 1, City, and Country
        const addressValid = formData.address_line_1.trim().length > 0 &&
                            formData.city.trim().length > 0 &&
                            formData.country.trim().length > 0;
        
        return basicFieldsValid && addressValid;
      case 1: // Content Strategy
        return strategyGenerated && 
               formData.brand_voice.length > 0 &&
               formData.target_audience.length > 0 &&
               formData.key_messages.length > 0 &&
               formData.content_goals.length > 0;
      case 2: // Preferred Platforms
        return formData.preferred_platform_ids.length > 0;
      default:
        return false;
    }
  };

  return {
    formData,
    setFormData,
    tagInputs,
    setTagInputs,
    addressInput,
    setAddressInput,
    strategyGenerated,
    setStrategyGenerated,
    handleInputChange,
    handleAddressSelect,
    handleTagInputChange,
    handleTagAdd,
    handleTagRemove,
    handleTagKeyPress,
    handlePlatformChange,
    validateStep,
  };
}

